import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalculateRequest } from '../../../services/legacyApi';
import { API_BASE_URL } from '../../../config/api';

type DefaultSetting = {
    setting_name: string;
    setting_value: string;
};

const defaultFallbacks: Record<string, string> = {
    'Default Labour Rate': '150',
    'Default Profit Margin %': '30',
    'Default Discount %': '0',
    'VAT Rate': '0',
    'Overhead Per SQM': '0',
    'Logistics Packing & Loading %': '2',
    'Logistics Freight %': '0',
    'Admin Contingency %': '1',
    'Admin Warranty %': '5',
    'Admin Retention %': '10',
    'Admin Banking & Finance %': '0',
    'Admin Other Costs %': '0',
    'Tooling Consumable %': '1',
    'Tooling Jigs & Fixtures %': '1',
    'Tooling Miscellaneous %': '1',
    'Electricity Cost %': '6',
    'Wastage %': '10',
    'Painting With Labour %': '0',
    'Acid Wash With Labour %': '0',
    'Styro Foam %': '0',
    'Production Accessories %': '0',
    'Skilled Manhours %': '50',
    'Unskilled Manhours %': '50',
    'Validity': '15 Days from the date of Quotation',
    'Annexure A': 'Standard Technical Annexure - GRP / FRP Marine Boxes\n' +
        'Description / Specification\n' +
        'Product: GRP / FRP Marine Box with Lid\n' +
        'Gelcoat Type: ISO Grade Gelcoat\n' +
        'Gelcoat Color: RAL3020\n' +
        'Resin Used: General Purpose (GP) Polyester Resin\n' +
        'Body Thickness: Sides & Lid: 4 mm, Bottom: 5 mm\n' +
        'Accessories Included:\n' +
        '- SS 304 Hinges - 2 Nos / Box\n' +
        '- SS 304 Toggle Latches - 2 Nos / Box\n' +
        '- Rubber Gasket - 1 Set / Box',
    'Annexure B': ''
};


let pendingCreate: Promise<any> | null = null;
let pendingCreatedAt = 0;

const toNumber = (val: string) => {
    const n = parseFloat(val);
    return Number.isFinite(n) ? n : 0;
};

const toFractionPercent = (val: string) => {
    const n = toNumber(val);
    return n >= 1 ? n / 100 : n;
};

const getSetting = (settings: Record<string, string>, key: string) =>
    settings[key] ?? defaultFallbacks[key] ?? '0';


export const NewEstimateRedirect: React.FC = () => {
    const navigate = useNavigate();
    const [error, setError] = useState<string | null>(null);
    const created = useRef(false);
    const [attempt, setAttempt] = useState(0);
    const apiBaseUrl = API_BASE_URL;

    useEffect(() => {
        if (created.current) return;
        let active = true;
        (async () => {
            try {
                const now = Date.now();
                if (!pendingCreate || now - pendingCreatedAt >= 15000) {
                    pendingCreatedAt = now;
                    pendingCreate = (async () => {
                        const controller = new AbortController();
                        const timeout = setTimeout(() => controller.abort(), 5000);
                        let data: DefaultSetting[] = [];
                        try {
                            const res = await fetch(`${apiBaseUrl}/system/settings/defaults`, { signal: controller.signal });
                            data = res.ok ? await res.json() : [];
                        } catch {
                            data = [];
                        } finally {
                            clearTimeout(timeout);
                        }

                        const dict = data.reduce<Record<string, string>>((acc, s) => {
                            acc[s.setting_name] = String(s.setting_value ?? '');
                            return acc;
                        }, {});

                        const payload: CalculateRequest = {
                            header: {
                                customer_id: undefined,
                                customer_name: undefined,
                                project_type_id: 1,
                                labour_rate_per_hour: toNumber(getSetting(dict, 'Default Labour Rate')),
                                overhead_per_sqm: toNumber(getSetting(dict, 'Overhead Per SQM')),
                                oh_per_sqm: toNumber(getSetting(dict, 'Overhead Per SQM')),
                                profit_margin: toFractionPercent(getSetting(dict, 'Default Profit Margin %')),
                                discount_percent: toFractionPercent(getSetting(dict, 'Default Discount %')),
                                vat_percent: toFractionPercent(getSetting(dict, 'VAT Rate')),

                                tooling_consumable_perc: toNumber(getSetting(dict, 'Tooling Consumable %')),
                                tooling_jigs_fixtures_perc: toNumber(getSetting(dict, 'Tooling Jigs & Fixtures %')),
                                tooling_misc_perc: toNumber(getSetting(dict, 'Tooling Miscellaneous %')),
                                logistics_packing_loading_perc: toNumber(getSetting(dict, 'Logistics Packing & Loading %')),
                                logistics_freight_cost_perc: toNumber(getSetting(dict, 'Logistics Freight %')),
                                admin_contigency_perc: toNumber(getSetting(dict, 'Admin Contingency %')),
                                admin_warranty_perc: toNumber(getSetting(dict, 'Admin Warranty %')),
                                admin_retention_perc: toNumber(getSetting(dict, 'Admin Retention %')),
                                admin_banking_finance_perc: toNumber(getSetting(dict, 'Admin Banking & Finance %')),
                                admin_other_cost_perc: toNumber(getSetting(dict, 'Admin Other Costs %')),
                                direct_electricity_perc: toNumber(getSetting(dict, 'Electricity Cost %')),
                                direct_wastage_perc: toNumber(getSetting(dict, 'Wastage %')),
                                direct_painting_labour_perc: toNumber(getSetting(dict, 'Painting With Labour %')),
                                direct_acid_wash_labour_perc: toNumber(getSetting(dict, 'Acid Wash With Labour %')),
                                direct_styro_foam: toNumber(getSetting(dict, 'Styro Foam %')),
                                direct_production_accessories: toNumber(getSetting(dict, 'Production Accessories %')),
                                skilled_manhours_perc: toNumber(getSetting(dict, 'Skilled Manhours %') || '50'),
                                unskilled_manhours_perc: toNumber(getSetting(dict, 'Unskilled Manhours %') || '50'),
                                non_working_manhours_perc: 0,

                                pm_design_cost: 0,
                                pm_project_manager_cost: 0,
                                pm_site_intervention_cost: 0,
                                pm_documentation_cost: 0,
                                pm_travel_cost: 0,

                                validity: getSetting(dict, 'Validity'),
                                annexure_a: getSetting(dict, 'Annexure A'),
                                annexure_b: '',
                            },
                            parts: [],
                        };

                        const createController = new AbortController();
                        const createTimeout = setTimeout(() => createController.abort(), 10000);
                        try {
                            const res = await fetch(`${apiBaseUrl}/estimates/`, {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify(payload),
                                signal: createController.signal,
                            });
                            if (!res.ok) {
                                const text = await res.text().catch(() => '');
                                throw new Error(text || 'Failed to create estimate');
                            }
                            return res.json();
                        } finally {
                            clearTimeout(createTimeout);
                        }
                    })();
                }

                const createdEstimate: any = await pendingCreate;
                if (!active) return;
                created.current = true;
                pendingCreate = null;
      navigate(`/legacy/estimates/${createdEstimate.estimate_id}?view=classic`);
            } catch (e: any) {
                if (!active) return;
                setError(e?.message || 'Failed to create estimate');
                pendingCreate = null;
            }
        })();
        return () => {
            active = false;
        };
    }, [navigate, attempt, apiBaseUrl]);

    return (
        <div className="p-8">
            <h1 className="text-xl font-bold">Creating new estimate…</h1>
            {error && <p className="text-sm text-red-600 mt-2">{error}</p>}
            {error && (
                <button
                    className="mt-4 px-4 py-2 rounded bg-blue-600 text-white text-sm font-semibold"
                    onClick={() => {
                        created.current = false;
                        setError(null);
                        pendingCreate = null;
                        setAttempt((a) => a + 1);
                    }}
                >
                    Retry
                </button>
            )}
        </div>
    );
};
